<template>
  <v-row align="center" justify="center">
    <v-col cols="12">
      <v-card shaped>
        <v-card-title>
          <v-icon large left color="primary">mdi-chart-box-outline</v-icon>
          <span class="title font-weight-light">{{ tabTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="loading.download" :disabled="loading.report" @click="Download">
            <v-icon left>mdi-microsoft-excel</v-icon>Скачать Excel
          </v-btn>
        </v-card-title>

        <v-tabs :value="tab" @change="GoTab" background-color="transparent" class="px-4">
          <v-tab href="#general">Общие показатели</v-tab>
          <v-tab href="#pharmacies">Закуп аптек</v-tab>
          <v-tab href="#distributors">Дистрибьюторы</v-tab>
        </v-tabs>

        <v-col>
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header>Немного об отчёте</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="text-justify mb-1">
                  Все три отчёта считаются по заказам сайта (без КМП и без заказов старого ФЦ), только со статусом
                  «Отправлен», по дате отправки. Данные — до вчера включительно (витрина наполняется ночью).
                  Период по умолчанию — текущий год и два предыдущих; в строках — суммы по каждому году периода.
                </p>
                <p class="text-justify mb-0">
                  «Закуп аптек» группируется по компании или по БИН заказа, строка раскрывается на логины.
                  «Дистрибьюторы»: место по сумме за период (равные суммы — одно место), тотал по регионам считается по
                  городу филиала, через который прошёл заказ; выбранный дистрибьютор выделяется и показывает своё место.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-form ref="form" @submit.prevent="Load">
          <v-row class="px-4">
            <v-col cols="12" sm="6" md="2">
              <v-text-field v-model="search.dateStart" type="date" label="Дата с" persistent-hint hint="Пусто — 1 января (год − 2)"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field v-model="search.dateEnd" type="date" label="Дата по" persistent-hint hint="Пусто — сегодня"></v-text-field>
            </v-col>
            <v-col v-if="tab === 'pharmacies'" cols="12" sm="6" md="2">
              <v-select v-model="search.groupBy" :items="groupByOptions" label="Группировать по"></v-select>
            </v-col>
            <v-col v-if="tab !== 'general'" cols="12" sm="6" md="2">
              <v-text-field v-model="search.search" label="Логин или компания" prepend-icon="mdi-magnify" clearable></v-text-field>
            </v-col>
            <v-col v-if="tab !== 'general'" cols="12" sm="6" md="2">
              <v-text-field v-model="search.bin" label="БИН заказа" clearable></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select v-model="search.cityIds" :items="options.cities" item-text="name" item-value="id" label="Регион (город)" multiple chips small-chips deletable-chips :loading="loading.options"></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-autocomplete v-model="search.distributorId" :items="options.distributors" item-text="name" item-value="id" :label="tab === 'distributors' ? 'Выбранный дистрибьютор' : 'Дистрибьютор'" clearable :loading="loading.options"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2" class="d-flex align-center">
              <v-btn color="primary" type="submit" :loading="loading.report" block>Показать</v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-row class="px-4" v-if="period.dateStart">
          <v-col class="caption grey--text pt-0">Период: {{ FormatDate(period.dateStart) }} — {{ FormatDate(period.dateEnd) }}</v-col>
        </v-row>

        <!-- Общие показатели -->
        <template v-if="tab === 'general'">
          <v-row class="px-4 pb-4">
            <v-col v-for="item in generalItems" :key="item.title" cols="12" sm="6" md="4">
              <v-card outlined>
                <v-card-text>
                  <div class="overline">{{ item.title }}</div>
                  <div class="text-h5">{{ item.value }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <!-- Закуп аптек -->
        <template v-if="tab === 'pharmacies'">
          <v-row class="px-4 pb-2">
            <v-col cols="auto"><v-chip color="primary" outlined>Сумма: {{ Money(pharmacies.total.amount) }}</v-chip></v-col>
            <v-col cols="auto"><v-chip color="primary" outlined>Упаковки: {{ pharmacies.total.quantity }}</v-chip></v-col>
            <v-col cols="auto"><v-chip color="primary" outlined>Заказы: {{ pharmacies.total.orders }}</v-chip></v-col>
            <v-col cols="auto"><v-chip outlined>Строк: {{ pharmacies.rows.length }}</v-chip></v-col>
          </v-row>
          <v-data-table
            :headers="pharmacyHeaders"
            :items="pharmacies.rows"
            :loading="loading.report"
            item-key="key"
            show-expand
            :expanded.sync="expanded"
            :items-per-page="50"
            :footer-props="{ 'items-per-page-options': [50, 100, 500, -1] }"
            class="elevation-1"
          >
            <template v-slot:item.amount="{ item }">{{ Money(item.amount) }}</template>
            <template v-slot:item.avgOrder="{ item }">{{ Money(item.avgOrder) }}</template>
            <template v-slot:item.lastPurchase="{ item }">{{ FormatDateTime(item.lastPurchase) }}</template>
            <template v-for="y in pharmacies.years" v-slot:[`item.year_${y}`]="{ item }">
              <span :key="y">{{ Money(YearAmount(item, y)) }}</span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Логин</th><th>Точка</th><th class="text-right">Сумма</th><th class="text-right">Упаковки</th>
                      <th class="text-right">Заказы</th><th class="text-right">Дни с закупками</th><th class="text-right">Средняя сумма</th>
                      <th v-for="y in pharmacies.years" :key="`h${y}`" class="text-right">{{ y }}</th><th>Последняя закупка</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in item.children" :key="c.userId">
                      <td>{{ c.login }}</td><td>{{ c.pharmacyName }}</td><td class="text-right">{{ Money(c.amount) }}</td>
                      <td class="text-right">{{ c.quantity }}</td><td class="text-right">{{ c.orders }}</td>
                      <td class="text-right">{{ c.purchaseDays }}</td><td class="text-right">{{ Money(c.avgOrder) }}</td>
                      <td v-for="y in pharmacies.years" :key="`c${c.userId}${y}`" class="text-right">{{ Money(YearAmount(c, y)) }}</td>
                      <td>{{ FormatDateTime(c.lastPurchase) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </td>
            </template>
          </v-data-table>
        </template>

        <!-- Дистрибьюторы -->
        <template v-if="tab === 'distributors'">
          <v-row class="px-4 pb-2">
            <v-col v-for="p in distributors.platform" :key="p.year" cols="auto">
              <v-chip color="primary" outlined>Площадка {{ p.year }}: {{ Money(p.amount) }}</v-chip>
            </v-col>
            <v-col v-if="distributors.selected" cols="auto">
              <v-chip color="green" dark>{{ distributors.selected.name }} — место {{ distributors.selectedPlace }}{{ distributors.selectedPlace > 10 ? " (не входит в ТОП-10)" : "" }}</v-chip>
            </v-col>
          </v-row>
          <v-card-subtitle class="pb-0">ТОП-10</v-card-subtitle>
          <v-data-table :headers="distributorHeaders" :items="distributors.top10" :loading="loading.report" hide-default-footer :items-per-page="-1" class="elevation-1 mb-6" dense :item-class="DistributorRowClass">
            <template v-slot:item.amount="{ item }">{{ Money(item.amount) }}</template>
            <template v-for="y in distributors.years" v-slot:[`item.year_${y}`]="{ item }">
              <span :key="y">{{ Money(YearAmount(item, y)) }}</span>
            </template>
          </v-data-table>
          <v-card-subtitle class="pb-0">Все дистрибьюторы и тотал по регионам</v-card-subtitle>
          <v-data-table :headers="distributorHeaders" :items="distributors.rows" :loading="loading.report" item-key="distributorId" show-expand :items-per-page="50" :footer-props="{ 'items-per-page-options': [50, 100, -1] }" class="elevation-1" :item-class="DistributorRowClass">
            <template v-slot:item.amount="{ item }">{{ Money(item.amount) }}</template>
            <template v-for="y in distributors.years" v-slot:[`item.year_${y}`]="{ item }">
              <span :key="y">{{ Money(YearAmount(item, y)) }}</span>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <v-simple-table dense>
                  <thead><tr><th>Регион (город филиала)</th><th class="text-right">Сумма</th><th class="text-right">Заказы</th></tr></thead>
                  <tbody>
                    <tr v-for="r in item.regions" :key="r.cityId"><td>{{ r.city }}</td><td class="text-right">{{ Money(r.amount) }}</td><td class="text-right">{{ r.orders }}</td></tr>
                  </tbody>
                </v-simple-table>
              </td>
            </template>
          </v-data-table>
        </template>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
const TABS = ["general", "pharmacies", "distributors"];

export default {
  props: { tab: { type: String, default: "general" } },
  data: () => ({
    search: { dateStart: "", dateEnd: "", groupBy: "company", search: "", bin: "", cityIds: [], distributorId: null },
    groupByOptions: [
      { text: "Компании", value: "company" },
      { text: "БИН", value: "bin" },
    ],
    options: { cities: [], distributors: [] },
    general: null,
    pharmacies: { rows: [], years: [], total: { amount: 0, quantity: 0, orders: 0 } },
    distributors: { rows: [], top10: [], platform: [], years: [], selected: null, selectedPlace: null },
    expanded: [],
    period: { dateStart: null, dateEnd: null },
    loading: { options: true, report: false, download: false },
  }),
  computed: {
    tabTitle() {
      return { general: "Общие показатели", pharmacies: "Закуп аптек", distributors: "Дистрибьюторы" }[this.tab] || "Закуп";
    },
    generalItems() {
      const g = this.general;
      if (!g) return [];
      return [
        { title: "Зарегистрированных аптечных логинов", value: g.registeredPharmacyLogins },
        { title: "Активных платных аптек (на конец периода)", value: g.activePaidPharmacies },
        { title: "Аптечных компаний (с платным комплектом)", value: g.pharmacyCompanies },
        { title: "Уникальных БИН (с платным комплектом)", value: g.pharmacyBins },
        { title: "Подключённых дистрибьюторов", value: g.connectedDistributors },
        { title: "Активных дистрибьюторов (с заказами за период)", value: g.activeDistributors },
        { title: "Закуп аптек, сумма", value: this.Money(g.purchaseAmount) },
        { title: "Закуп аптек, упаковки", value: g.purchaseQuantity },
        { title: "Закуп аптек, заказы", value: g.purchaseOrders },
      ];
    },
    pharmacyHeaders() {
      const base = [
        { text: this.search.groupBy === "bin" ? "БИН" : "Компания", value: "title" },
        { text: this.search.groupBy === "bin" ? "Компания" : "БИН", value: this.search.groupBy === "bin" ? "companyName" : "bin" },
        { text: "Регион", value: "cities" },
        { text: "Логинов", value: "logins", align: "end" },
        { text: "Точек", value: "points", align: "end" },
        { text: "Комплекты", value: "complects" },
        { text: "Сумма", value: "amount", align: "end" },
        { text: "Упаковки", value: "quantity", align: "end" },
        { text: "Заказы", value: "orders", align: "end" },
        { text: "Дни с закупками", value: "purchaseDays", align: "end" },
        { text: "Средняя сумма", value: "avgOrder", align: "end" },
      ];
      const years = (this.pharmacies.years || []).map((y) => ({ text: String(y), value: `year_${y}`, align: "end", sortable: false }));
      return [...base, ...years, { text: "Последняя закупка", value: "lastPurchase" }, { text: "", value: "data-table-expand" }];
    },
    distributorHeaders() {
      const base = [
        { text: "Место", value: "place", align: "end" },
        { text: "Дистрибьютор", value: "name" },
        { text: "Сумма", value: "amount", align: "end" },
        { text: "Упаковки", value: "quantity", align: "end" },
        { text: "Заказы", value: "orders", align: "end" },
        { text: "Комплекты", value: "complects" },
      ];
      const years = (this.distributors.years || []).map((y) => ({ text: String(y), value: `year_${y}`, align: "end", sortable: false }));
      return [...base, ...years, { text: "", value: "data-table-expand" }];
    },
  },
  watch: {
    tab(value) {
      if (TABS.includes(value)) this.Load();
    },
  },
  async mounted() {
    await this.GetFilterOptions();
    if (TABS.includes(this.tab)) this.Load();
  },
  methods: {
    GoTab(tab) {
      const paths = { general: "/purchasegeneral", pharmacies: "/purchasepharmacies", distributors: "/purchasedistributors" };
      if (tab && tab !== this.tab && paths[tab]) this.$router.push({ path: paths[tab] });
    },
    async GetFilterOptions() {
      this.loading.options = true;
      try {
        this.options = await this.$store.dispatch("PURCHASE_FILTER_OPTIONS");
      } catch (error) {
        window.console.log(error);
      } finally {
        this.loading.options = false;
      }
    },
    Request() {
      const s = this.search;
      return {
        dateStart: s.dateStart || null,
        dateEnd: s.dateEnd || null,
        groupBy: s.groupBy,
        search: s.search || null,
        bin: s.bin || null,
        cityIds: s.cityIds,
        distributorId: s.distributorId,
      };
    },
    async Load() {
      this.loading.report = true;
      try {
        if (this.tab === "general") {
          this.general = await this.$store.dispatch("PURCHASE_GENERAL", this.Request());
          this.period = { dateStart: this.general.dateStart, dateEnd: this.general.dateEnd };
        } else if (this.tab === "pharmacies") {
          this.pharmacies = await this.$store.dispatch("PURCHASE_PHARMACIES", this.Request());
          this.expanded = [];
          this.period = { dateStart: this.pharmacies.dateStart, dateEnd: this.pharmacies.dateEnd };
        } else {
          this.distributors = await this.$store.dispatch("PURCHASE_DISTRIBUTORS", this.Request());
          this.period = { dateStart: this.distributors.dateStart, dateEnd: this.distributors.dateEnd };
        }
      } catch (error) {
        window.console.log(error);
      } finally {
        this.loading.report = false;
      }
    },
    Download() {
      const action = { general: "DOWNLOAD_PURCHASE_GENERAL", pharmacies: "DOWNLOAD_PURCHASE_PHARMACIES", distributors: "DOWNLOAD_PURCHASE_DISTRIBUTORS" }[this.tab];
      this.loading.download = true;
      this.$store
        .dispatch(action, this.Request())
        .catch((error) => window.console.log(error))
        .finally(() => {
          this.loading.download = false;
        });
    },
    YearAmount(item, year) {
      const yv = (item.yearValues || []).find((x) => x.year === year);
      return yv ? yv.amount : 0;
    },
    DistributorRowClass(item) {
      return this.distributors.selected && item.distributorId === this.distributors.selected.distributorId ? "font-weight-bold green lighten-5" : "";
    },
    Money(value) {
      return Number(value || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    FormatDate(value) {
      return value ? this.$options.filters.toDate(String(value).substr(0, 10)) : "";
    },
    FormatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("ru-RU", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>
